import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { JobStatus } from '../types/admin';

export interface JobsDashboardKpis {
  totalJobs: number;
  scheduledCount: number;
  inProgressCount: number;
  completedCount: number;
  onHoldCount: number;
  cancelledCount: number;
  pendingAssignment: number;
  avgCrewSize: number;
  avgEstimatedHours: number;
  // Operations KPIs
  activeJobs: number;
  fleetUtilization: number;
  completionRate: number;
}

export interface JobsMonthlyData {
  month: string;
  count: number;
}

export interface JobsStatusItem {
  status: string;
  count: number;
  label: string;
}

export interface MoverData {
  uid: string;
  name: string;
  count: number;
}

export interface MethodData {
  method: string;
  count: number;
}

export interface UpcomingJob {
  id: string;
  customerCompanyName: string;
  jobStatus: JobStatus;
  moveDate: string;
  crewLeadName: string;
  crewSize: number;
  truckCount: number;
  assignmentStatus: 'ready' | 'partial' | 'unassigned';
}

export interface CrewSizeData {
  size: string;
  count: number;
}

export interface CrewSizeMonthlyData {
  month: string;
  avgCrewSize: number;
}

export interface FleetStatus {
  onJob: number;
  atWarehouse: number;
  maintenance: number;
  inactive: number;
  total: number;
}

export interface AvailableMover {
  uid: string;
  name: string;
}

export interface JobsDashboardData {
  kpis: JobsDashboardKpis;
  jobsOverTime: JobsMonthlyData[];
  statusDistribution: JobsStatusItem[];
  jobsByMover: MoverData[];
  methodDistribution: MethodData[];
  upcomingJobs: UpcomingJob[];
  crewSizeDistribution: CrewSizeData[];
  crewSizeOverTime: CrewSizeMonthlyData[];
  fleetStatus: FleetStatus;
  availableMovers: AvailableMover[];
}

export type JobsDashboardPeriod = '7d' | '30d' | '90d' | '12m' | 'all';

interface JobsDashboardResponse {
  success: boolean;
  data: JobsDashboardData;
}

export function useJobsDashboard(orgId: string | null, period: JobsDashboardPeriod, moverId?: string) {
  const [data, setData] = useState<JobsDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboard = useCallback(async () => {
    if (!orgId) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('period', period);
      if (moverId) params.set('moverId', moverId);
      const res = await api.get<JobsDashboardResponse>(`/dashboard/${orgId}/jobs?${params}`);
      setData(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load jobs dashboard');
    } finally {
      setLoading(false);
    }
  }, [orgId, period, moverId]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return { data, loading, error, refetch: fetchDashboard };
}
